import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthHeader } from "services/authHeader";
import { RejectedValue, ErrorResponse, MainResponse } from "shared/interfaces/core-interfaces";
import {
    ProjectFileUploadResponse,
    ProjectFileUploadDetailedResponse,
    ProjectFilesCreateDataResponse,
    InitializeUploadRequest,
    InitializeFileUploadResponse,
    CompleteUploadRequest,
    CompleteUploadResponse,
    AbortUploadRequest,
    AppendChunkResponse,
    initialProjectFilesState,
} from "./projectFilesInterfaces";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const GetAllProjectFileUploads = createAsyncThunk<ProjectFileUploadResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/GetAllProjectFileUploads',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/getall', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProjectFileUploadResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetProjectFileUploadsByEmployee = createAsyncThunk<ProjectFileUploadResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/GetProjectFileUploadsByEmployee',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/getbyemployee', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProjectFileUploadResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetProjectFilesCreateData = createAsyncThunk<ProjectFilesCreateDataResponse, void, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/GetProjectFilesCreateData',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProjectFilesCreateDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const InitializeUpload = createAsyncThunk<InitializeFileUploadResponse, InitializeUploadRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/InitializeUpload',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/initialize', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as InitializeFileUploadResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const AppendChunk = createAsyncThunk<AppendChunkResponse, { fileId: string; partNumber: number; projectId: string; uploadId: string; chunk: Blob }, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/AppendChunk',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(
            baseUri + `api/project-file-upload/append/${request.fileId}/${request.partNumber}?projectId=${request.projectId}&uploadId=${request.uploadId}`,
            {
                method: "PUT",
                headers: {
                    ...AuthHeader(false),
                    'Content-Type': 'application/octet-stream',
                },
                body: request.chunk
            }
        );
        if (response.status === 200) {
            const responseModel = await response.json() as AppendChunkResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CompleteUpload = createAsyncThunk<CompleteUploadResponse, CompleteUploadRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/CompleteUpload',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/complete', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAllProjectFileUploads());
            const responseModel = await response.json() as CompleteUploadResponse;
            dispatch(showNotification({ message: "File uploaded successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const AbortUpload = createAsyncThunk<MainResponse, AbortUploadRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/AbortUpload',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/abort', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Upload aborted.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetProjectFileUpload = createAsyncThunk<ProjectFileUploadDetailedResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/GetProjectFileUpload',
    async (fileId, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/get?id=' + fileId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProjectFileUploadDetailedResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadProjectFile = createAsyncThunk<void, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/DownloadProjectFile',
    async (fileId, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/download/' + fileId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.ok) {
            const link = document.createElement('a');
            link.href = response.url;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DisablePublicAccessProjectFile = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/DisablePublicAccessProjectFile',
    async (fileId, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/deactivate/' + fileId, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetAllProjectFileUploads());
            dispatch(showNotification({ message: responseModel.message ?? "Public access disabled.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const EnablePublicAccessProjectFile = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectFilesSlice/EnablePublicAccessProjectFile',
    async (fileId, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/project-file-upload/reactivate/' + fileId, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetAllProjectFileUploads());
            dispatch(showNotification({ message: responseModel.message ?? "Public access enabled.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ProjectFilesSlice = createSlice({
    name: 'ProjectFilesSlice',
    initialState: initialProjectFilesState,
    reducers: {
        resetProjectFilesSlice: (state) => {
            state.fileUploads = [];
            state.createData = null;
        },
        resetProjectFileUpload: (state) => {
            state.fileUpload = null;
        },
        copyProjectFileDownloadLink: (state, action: PayloadAction<string>) => {
            // The public download page (features/PublicDownload), not the file itself.
            navigator.clipboard.writeText(window.location.origin + '/download/' + action.payload);
        }
    },
    extraReducers(builder) {
        builder.addCase(GetAllProjectFileUploads.fulfilled, (state, action: PayloadAction<ProjectFileUploadResponse[]>) => {
            state.fileUploads = action.payload;
        });
        builder.addCase(GetAllProjectFileUploads.rejected, (state) => {
            state.fileUploads = [];
        });
        builder.addCase(GetProjectFileUploadsByEmployee.fulfilled, (state, action: PayloadAction<ProjectFileUploadResponse[]>) => {
            state.fileUploads = action.payload;
        });
        builder.addCase(GetProjectFilesCreateData.fulfilled, (state, action: PayloadAction<ProjectFilesCreateDataResponse>) => {
            state.createData = action.payload;
        });
        builder.addCase(GetProjectFileUpload.fulfilled, (state, action: PayloadAction<ProjectFileUploadDetailedResponse>) => {
            state.fileUpload = action.payload;
        });
    }
});

export const { resetProjectFilesSlice, resetProjectFileUpload, copyProjectFileDownloadLink } = ProjectFilesSlice.actions;
export default ProjectFilesSlice.reducer;
